import { ImpactLensType } from '@upright/common/impact-lenses/types';
import { ModelEntityType } from '@upright/common/model/input/types';
import { friendlyDimensionName } from '@upright/common/model/texts/friendly-dimension-name';
import { Dimension, orderedDimensions } from '@upright/common/model/types';
import { openChatPanel } from '@upright/ui/chat/chat-panel-state';
import { dimensionIcon } from '@upright/ui/common/generic/components/icons/dimensions';
import { bouTextFont } from '@upright/ui/common/generic/content/texts/bou-text/text-style';
import { BouColor } from '@upright/ui/common/generic/global/bou-colors';
import { useIsTeam } from '@upright/ui/common/generic/hooks';
import { zeroWidthNonBreakingSpace } from '@upright/ui/common/generic/utilities/zero-width-space';
import { DimensionTitleNetFigure } from '@upright/ui/common/upright/core/impact-lens-profile/frame/dimension-titles/dimension-title-net-figure';
import {
  buildDrivingImpactPrompt,
  dimensionAdjective,
} from '@upright/ui/common/upright/core/impact-lens-profile/frame/chat-prompt';
import { buildNetImpactDrillInPayload } from '@upright/ui/common/upright/core/impact-lenses/net-impact/drill-in-panel/build-payload';
import { openNetImpactDrillIn } from '@upright/ui/common/upright/core/impact-lenses/net-impact/drill-in-panel/state';
import { NetImpactLensSelectionState } from '@upright/ui/common/upright/core/impact-lenses/net-impact/selection-state';
import {
  NetImpactLensData,
  NetImpactLensOptions,
} from '@upright/ui/common/upright/core/impact-lenses/net-impact/types';
import { useReleaseFeatureGate } from '@upright/ui/common/upright/model/output/releases/hooks';
import { CSSProperties } from 'react';
import styled, { css } from 'styled-components';
import { useCarouselContext, useCarouselLayout } from '../../context';

export function DimensionTitlesWithIcons() {
  const titles = orderedDimensions.map((d, _index) => {
    return <DimensionCenterArea key={d} dimension={d} />;
  });
  return <>{titles}</>;
}

interface Props {
  dimension: Dimension;
}

/**
 * PLT-4700 follow-up: turn the dimension label readable text (e.g.
 * "Environment", "Society", "Health", "Knowledge") rendered in the
 * center column of the net-impact carousel into a clickable affordance
 * that opens the right-side chat panel with a pre-filled "what is
 * driving the <dimension> impact of <company>?" prompt.
 *
 * Mirrors the dimension-bar click handler in
 * `impact-lenses/net-impact/data-area/dimension-bar.tsx` (which already
 * routes the colored bars next to these labels into chat for team users):
 * the colored bar is one entry-point, the label text itself was the
 * obvious missing one — users were trying to click the visible label
 * before they noticed the smaller bar. Now both work the same way.
 *
 * Two paths share this handler (see the gates in the body):
 *   - Company net-impact profile → opens the drill-in panel for every
 *     viewer (PLT-5427); the panel's Chat tab handles per-user access.
 *   - Group / portfolio net-impact → team-only chat deep-link (those
 *     entities have no per-company drilldown panel). The chat URL needs
 *     an `entity` binding to target the model's tool calls.
 * Non-net-impact lenses share this component but have no chat tool, so
 * the label stays decoration there.
 *
 * The prompt format ("What is driving the <dimension-adjective> impact
 * of <name>?") matches the dimension-bar handler's wording but drops
 * the valence qualifier — clicking the *label* is the un-valenced "tell
 * me about this dimension" entry-point, whereas clicking the colored
 * positive or negative bar carries that valence into the prompt.
 */
function DimensionCenterArea(props: Props) {
  const layout = useCarouselLayout();
  const carousel = useCarouselContext();
  const isTeam = useIsTeam();
  const area = layout.dimensionHeadCenterArea(props.dimension);

  const style: CSSProperties = {
    top: area.y1,
    left: area.x1,
    width: area.width,
    height: area.height,
  };

  const Icon = dimensionIcon(props.dimension);

  const entity = carousel.data.entity;
  const isNetImpactLens = carousel.lensType === ImpactLensType.UPRIGHT_NET_IMPACT;
  const isCompany = entity?.type === ModelEntityType.COMPANY;
  // PLT-4940 / PLT-5427: clicking the dimension label on a company
  // net-impact profile opens the dimension-grain drill-in panel (matching
  // the dimension bar), now for every viewer — the per-user variation
  // lives inside the panel's Chat tab. Group / portfolio labels keep the
  // team-only chat deep-link.
  const opensDrillIn = isNetImpactLens && isCompany;
  const opensChat = isTeam && isNetImpactLens && entity !== undefined && !isCompany;
  const clickable = opensDrillIn || opensChat;
  // Mirror the impact-bar's value-chain gate so the drill-in panel's
  // contributors table splits by value chain exactly when the chart does.
  const allowValueChains = useReleaseFeatureGate('value-chain-splits');
  const showValueChains =
    Boolean((carousel.selectionState as NetImpactLensSelectionState).showValueChains) && allowValueChains;

  function handleClick() {
    if (!entity) return;
    if (opensDrillIn) {
      const netOptions = carousel.options as NetImpactLensOptions;
      const netData = carousel.data as NetImpactLensData;
      openNetImpactDrillIn({
        payload: buildNetImpactDrillInPayload({
          target: { grain: 'dimension', dimension: props.dimension },
          entity: { id: entity.id, type: entity.type, name: entity.name },
          scoreReader: netOptions.scoreReader,
          revenue: netData.revenue,
          modelResultId: netData.modelResultId,
          productNames: netData.productNames,
          hasProductViewAccess: netData.hasProductViewAccess,
          showValueChains,
        }),
        // The label is un-valenced; `openNetImpactDrillIn` defaults to the
        // positive contributors tab.
      });
      return;
    }
    if (!opensChat) return;
    const entityName = entity.name ?? 'this entity';
    // PLT-4700 (Slack thread, "Same for everything"): one prompt shape
    // across all four net-impact click surfaces — the dimension label
    // here, the dimension bar, the impact-category title, and the
    // impact-category bar all route into the same contributing-products
    // card via `buildDrivingImpactPrompt`. See `chat-prompt.ts` for the
    // full rationale.
    const prompt = buildDrivingImpactPrompt(dimensionAdjective(props.dimension), entityName);
    const pageHref =
      typeof window !== 'undefined'
        ? `${window.location.pathname}${window.location.search}${window.location.hash}`
        : '';
    openChatPanel({
      prompt,
      entityId: entity.id,
      entityType: entity.type,
      source: 'dimension_bar',
      pageContext: { label: `${entityName} — Net impact`, href: pageHref, lensLabel: 'Net impact' },
    });
  }

  return (
    <>
      <Bg style={style}>{zeroWidthNonBreakingSpace}</Bg>
      <Root style={style} clickable={clickable} onClick={handleClick}>
        <Icon height={area.height * 0.4} />
        <Title style={{ marginLeft: '0.5em' }}>
          {friendlyDimensionName(props.dimension)}
          <DimensionTitleNetFigure dimension={props.dimension} />
        </Title>
      </Root>
    </>
  );
}

// PLT-4700: `dimensionWord` moved to `frame/chat-prompt.ts` as
// `dimensionAdjective`. See that module for why the prompt shape +
// dimension-adjective table is now shared across all four net-impact
// click surfaces.

const Bg = styled.div`
  position: absolute;
  background-color: white;
`;

export const Root = styled.div<{ clickable?: boolean }>`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  background-color: ${BouColor.TOOL_TABLE_BACKGROUND_LIGHT_GRAY};
  background-color: ${BouColor.TOOL_BACKGROUND_LIGHT_GRAY};
  background-color: rgb(240, 240, 240);
  background-color: black;

  background-color: #2c2c2c;
  color: white;

  background-color: rgba(114, 239, 221, 0.2);
  //background-color: ${BouColor.YELLOW};

  //background-color: rgba(128, 255, 217, 1.0);

  background-color: rgba(72, 192, 227, 0.1);
  background-color: rgba(255, 255, 227, 0.1);
  background-color: hsla(220, 100%, 75%, 0.1);
  background-color: rgba(128, 171, 255, 0.1);

  /* Everything above is overridden: the last background-color in the rule is the one
     that paints, so this — and not any of the alternatives parked above it — is the
     tile's actual colour. Changing one of those does nothing, which is worth knowing
     before "fixing" a colour here. (The hsla() above was written as
     'hsl(220, 100, 75, 0.1)', missing the percent signs and so invalid; corrected in
     PLT-6037 for the reader's sake, not for any pixel.)

     Note for the next editor, human or agent: this is a styled-components template
     literal, so a backtick in a CSS comment ends the template and the file stops
     parsing. Quote CSS in these comments with apostrophes. */
  background-color: rgb(247, 247, 247);
  color: ${BouColor.PLATFORM_TEXT_BLACK};
  //background-color: rgba(116, 0, 184, 1.0);
  /*color: white;*/
  border-radius: 2px;
  /* PLT-4700 follow-up: clickable affordance when the dimension label is
     wired to open the chat panel (team users on the net-impact lens with
     a bound entity). Hover background mirrors the slight darken used on
     other clickable rows in the lens profile. */
  ${p =>
    p.clickable
      ? css`
          cursor: pointer;
          transition: background-color 0.12s ease;
          &:hover {
            background-color: rgb(235, 235, 235);
          }
        `
      : ''};
`;

export const Title = styled.div`
  ${bouTextFont};
  font-weight: 600;
`;
